/**
 * @fileoverview Connect a recording activity to gated transcription and recorded transcript/summary reads. Backend recording identity, feature admission, and AI execution own generation. This view distinguishes explicit transcription requests from opening an existing result. Audio access and generated text retain separate permission/storage boundaries.
 */

import React, { useEffect, useState } from 'react';
import api from '../../../api';
import Button from '../../shared/AppPrimeButton';
import { AppModal } from '../../shared';

let cachedEnabled = null;
let pending = null;

 
export function fetchCallTranscriptionEnabled() {
  if (cachedEnabled !== null) return Promise.resolve(cachedEnabled);
  if (!pending) {
    // Promise.resolve guards callers (and test mocks) whose api.get returns nothing.
    pending = Promise.resolve()
      .then(() => api.get('/call-transcription/config'))
      .then((response) => {
        cachedEnabled = Boolean(response?.data?.enabled);
        return cachedEnabled;
      })
      .catch(() => {
        pending = null;
        return false;
      });
  }
  return pending;
}

 
export function resetCallTranscriptionEnabledCache() {
  cachedEnabled = null;
  pending = null;
}

const STATUS_TONES = { pending: 'info', processing: 'info', failed: 'danger', skipped: 'neutral' };

const STATUS_LABELS = {
  pending: 'Transcription queued',
  processing: 'Transcribing…',
  failed: 'Transcription failed',
  skipped: 'Not transcribed',
};



function RecordingTranscript({ activity }) {
  const [enabled, setEnabled] = useState(cachedEnabled === true);
  const [status, setStatus] = useState(activity.TranscriptStatus || null);
  const [modalOpen, setModalOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCallTranscriptionEnabled().then((value) => { if (active) setEnabled(value); });
    return () => { active = false; };
  }, []);

  useEffect(() => { setStatus(activity.TranscriptStatus || null); }, [activity.TranscriptStatus]);

  if (!enabled || !activity.PhoneRecordingID) return null;

   
  const requestTranscription = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/activity/recordings/${activity.PhoneRecordingID}/transcribe`);
      setStatus(response.data?.status || 'pending');
    } catch (error) {
      setStatus('failed');
    } finally {
      setBusy(false);
    }
  };

   
  const openTranscript = async () => {
    setModalOpen(true);
    try {
      const response = await api.get(`/activity/recordings/${activity.PhoneRecordingID}/transcript`);
      setTranscript(response.data);
    } catch (error) {
      setTranscript({ error: 'Could not load the transcript.' });
    }
  };

  return (
    <div className="activity-recording-transcript">
      {status === 'completed' ? (
        <>
          <div className="activity-recording-transcript__meta">
            <span className="activity-pill activity-pill--info">AI summary</span>
            <Button label="View transcript" icon="pi pi-file" className="p-button-text p-button-sm activity-recording-transcript__action" onClick={openTranscript} />
          </div>
          <p className="activity-recording-transcript__summary">{activity.TranscriptSummary}</p>
        </>
      ) : (
        <div className="activity-recording-transcript__meta">
          {status && <span className={`activity-pill activity-pill--${STATUS_TONES[status] || 'neutral'}`}>{STATUS_LABELS[status] || status}</span>}
          {status === 'skipped' && activity.TranscriptSkipReason && (
            <span className="activity-recording-transcript__reason">{activity.TranscriptSkipReason}</span>
          )}
          {(!status || status === 'failed') && (
            <Button
              label={busy ? 'Queuing…' : 'Transcribe'}
              icon="pi pi-microphone"
              className="p-button-sm p-button-outlined activity-recording-transcript__action"
              onClick={requestTranscription}
              disabled={busy}
            />
          )}
        </div>
      )}
      <AppModal visible={modalOpen} onClose={() => setModalOpen(false)} title="Call transcript" description="Machine-generated. Privileged and confidential; verify against the recording.">
        {!transcript && <p className="activity-recording-transcript__reason">Loading…</p>}
        {transcript?.error && <p className="activity-recording-transcript__reason">{transcript.error}</p>}
        {transcript && !transcript.error && (
          <div className="activity-recording-transcript__dialog">
            <span className="activity-recording-transcript__label">AI summary</span>
            <p className="activity-recording-transcript__summary">{transcript.summary}</p>
            <span className="activity-recording-transcript__label">Transcript{transcript.transcription_model ? ` · ${transcript.transcription_model}` : ''}</span>
            <div className="activity-recording-transcript__text">{transcript.transcript}</div>
          </div>
        )}
      </AppModal>
    </div>
  );
}

export default RecordingTranscript;
